import React, {
  createContext,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { GlobalStyles, Tooltip } from '@mui/material';
import { alpha, darken } from '@mui/material/styles';
import type { CSSObject, Theme } from '@mui/material/styles';
import { ACTION_COLORS, BG_COLORS, TEXT_COLORS } from '../../utils/colors';
import { FONT_SIZES } from '../../utils/responsive';
import { ROW_STRIPE_BG } from '../../theme/sxPresets';

/**
 * Лёгкие кирпичики ячеек гриды игровых данных.
 *
 * **Почему не MUI.** При быстрой прокрутке виртуализированная таблица за кадр подменяет весь экран
 * строк (~45 строк × ~10 ячеек). Каждый `Typography` / `Box` / `TableCell` / `Tooltip` / `Checkbox` —
 * styled-компонент, который на каждой отрисовке заново собирает стили. Замер 10.09.2026: на строку
 * ~55 таких компонентов (234 узла React), экран строк рисовался 120–470 мс (dev) — прокрутка не
 * успевала за ползунком. Здесь то же самое на вид — обычная разметка с классами `gd-*`, стили которых
 * собраны ОДИН раз (`GameDataCellStyles`, смонтирован в `AdminLayout`).
 *
 * **Правило для строк гриды:** внутри строки — только эти кирпичики и обычная разметка с классами
 * `gd-*`. MUI — в шапке, редакторах и диалогах: они монтируются по одному, а не на каждую строку.
 * Вид кирпичиков снят с живых MUI-компонентов этой темы и повторяет их один в один.
 */

/** Подсветка строки: конфликт при массовой правке — жёлтым, только что сохранённая — зелёным. */
export type RowTone = 'conflict' | 'saved';

export const TONE_BG: Record<RowTone, string> = {
  conflict: 'rgba(245, 158, 11, 0.14)',
  saved: 'rgba(34, 197, 94, 0.10)',
};

export const TONE_BORDER: Record<RowTone, string> = {
  conflict: 'rgba(245, 158, 11, 0.85)',
  saved: 'rgba(34, 197, 94, 0.7)',
};

/** Заливка ячейки, которую правили в этом браузере (`useChangedCells`), и она же на наведении. */
export const CHANGED_BG = 'rgba(34, 197, 94, 0.14)';
const CHANGED_BG_HOVER = 'rgba(34, 197, 94, 0.2)';

/**
 * Выделение клеток — белое, по правилу «никакого синего в интерактиве» (docs/ui-patterns.md §6).
 * Сначала было белым на 9% без рамки — на тёмной таблице его не было видно вовсе; потом синим, как
 * в Google Sheets, — читалось, но нарушало правило. Теперь белое, но заметное: плотнее заливка и
 * белая рамка вокруг всего диапазона (владелец, 24.09.2026 — «попробуй, если что скажу вернуть»).
 */
const SELECTION_TINT = 'rgba(255, 255, 255, 0.16)';
const SELECTION_BORDER = 'rgba(255, 255, 255, 0.9)';
const NO_SHADOW = '0 0 transparent';

const cx = (...names: Array<string | false | null | undefined>): string => names.filter(Boolean).join(' ');

/** Класс `<td>` гриды: вид как у `TableCell size="small"`, выравнивание — модификатором. */
export const gdCellClass = (align?: string, extra?: string): string =>
  cx('gd-td', align === 'right' && 'gd-td--right', align === 'center' && 'gd-td--center', extra);

// ---------------------------------------------------------------------------------------------
// Текст
// ---------------------------------------------------------------------------------------------

export interface CellTextProps extends React.HTMLAttributes<HTMLParagraphElement> {
  /** Цвет: основной, приглушённый, «пусто». Не задан — наследуется от ячейки. */
  tone?: 'primary' | 'muted' | 'disabled';
  bold?: boolean;
  /** Мелкая подпись — `FONT_SIZES.smallCaption`. */
  small?: boolean;
  /** В одну строку с многоточием. */
  noWrap?: boolean;
}

/** Текст ячейки — вместо `Typography` (тот же `body1`, те же цвета). */
export const CellText = React.forwardRef<HTMLParagraphElement, CellTextProps>(function CellText(
  { tone, bold, small, noWrap, className, ...rest },
  ref,
) {
  return (
    <p
      ref={ref}
      className={cx(
        'gd-text',
        tone && `gd-text--${tone}`,
        bold && 'gd-text--bold',
        small && 'gd-text--small',
        noWrap && 'gd-text--nowrap',
        className,
      )}
      {...rest}
    />
  );
});

// ---------------------------------------------------------------------------------------------
// Подсказки: одна на всю гриду
// ---------------------------------------------------------------------------------------------

/** Внутри гриды подсказки ячеек показывает один общий тултип — см. `useCellTips`. */
const CellTipScopeContext = createContext(false);

/** Область, где `CellTip` отдаёт подсказку общему тултипу гриды. */
export const CellTipScope: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <CellTipScopeContext.Provider value>{children}</CellTipScopeContext.Provider>
);

export interface CellTipProps {
  title?: string | null;
  /** Обычный элемент (`div`, `span`, `button`, `CellText`) — ему вешается подсказка. */
  children: React.ReactElement;
}

/**
 * Подсказка на наведение.
 *
 * В гриде — только метка `data-gd-tip` на элементе: показывает её один общий тултип (`useCellTips`).
 * Свой `Tooltip` в каждой ячейке — это ещё несколько компонентов и закрытый `Popper` на каждую строку.
 * Вне гриды (карточки на телефоне, диалоги) — обычный MUI `Tooltip`, вид тот же.
 */
export const CellTip: React.FC<CellTipProps> = ({ title, children }) => {
  const scoped = useContext(CellTipScopeContext);
  if (!title) return children;
  if (scoped) return React.cloneElement(children, { 'data-gd-tip': title } as Record<string, unknown>);
  return <Tooltip title={title}>{children}</Tooltip>;
};

interface CellTipApi {
  show: (anchor: HTMLElement, title: string) => void;
  hide: () => void;
}

interface CellTipState {
  anchor: HTMLElement | null;
  title: string;
  open: boolean;
}

/** Сам общий тултип. Обычный MUI `Tooltip` — вид как у всех; якорь — наведённый элемент. */
const CellTipLayer = React.memo(
  React.forwardRef<CellTipApi>(function CellTipLayer(_props, ref) {
    const [tip, setTip] = useState<CellTipState>({ anchor: null, title: '', open: false });
    useImperativeHandle(
      ref,
      () => ({
        show: (anchor, title) => setTip({ anchor, title, open: true }),
        // Текст оставляем до конца анимации закрытия, иначе на миг мелькнёт пустая плашка
        hide: () => setTip((prev) => (prev.open ? { ...prev, open: false } : prev)),
      }),
      [],
    );
    return (
      <Tooltip open={tip.open} title={tip.title} slotProps={{ popper: { anchorEl: tip.anchor } }}>
        <span hidden />
      </Tooltip>
    );
  }),
);

/** Как у MUI `Tooltip`: первая подсказка — через 100 мс, следующая подряд — сразу. */
const TIP_ENTER_DELAY = 100;
const TIP_WARM_WINDOW = 800;

/**
 * Один тултип на всю гриду: слушает наведение на контейнере и показывает подсказку того элемента,
 * на котором стоит `data-gd-tip` (её ставит `CellTip`). Прокрутка и клик прячут подсказку — строка
 * под ней может исчезнуть (виртуализация) или открыться на правку.
 */
export function useCellTips() {
  const apiRef = useRef<CellTipApi>(null);
  const timer = useRef<number | undefined>(undefined);
  const shown = useRef<HTMLElement | null>(null);
  const pending = useRef<HTMLElement | null>(null);
  const hiddenAt = useRef(0);

  const handlers = useMemo(() => {
    const hide = () => {
      window.clearTimeout(timer.current);
      pending.current = null;
      if (!shown.current) return;
      shown.current = null;
      hiddenAt.current = performance.now();
      apiRef.current?.hide();
    };
    const showNow = (anchor: HTMLElement) => {
      pending.current = null;
      shown.current = anchor;
      apiRef.current?.show(anchor, anchor.dataset.gdTip ?? '');
    };
    return {
      onMouseOver: (event: React.MouseEvent) => {
        const anchor = (event.target as Element).closest<HTMLElement>('[data-gd-tip]');
        if (anchor === shown.current || (anchor && anchor === pending.current)) return;
        window.clearTimeout(timer.current);
        pending.current = null;
        if (!anchor) {
          hide();
          return;
        }
        const warm = shown.current !== null || performance.now() - hiddenAt.current < TIP_WARM_WINDOW;
        if (warm) {
          showNow(anchor);
          return;
        }
        pending.current = anchor;
        timer.current = window.setTimeout(() => {
          if (pending.current === anchor && anchor.isConnected) showNow(anchor);
        }, TIP_ENTER_DELAY);
      },
      onMouseOut: (event: React.MouseEvent) => {
        const from = (event.target as Element).closest<HTMLElement>('[data-gd-tip]');
        if (!from) return;
        const to = event.relatedTarget as Node | null;
        // Курсор ушёл внутрь того же элемента (на иконку в ссылке) — подсказка остаётся
        if (to && from.contains(to)) return;
        if (from === shown.current || from === pending.current) hide();
      },
      onMouseDown: hide,
      onScroll: hide,
    };
  }, []);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  // Слой один на гриду и сам по себе не перерисовывается
  const layer = useMemo(() => <CellTipLayer ref={apiRef} />, []);
  return { handlers, layer };
}

// ---------------------------------------------------------------------------------------------
// Управляющие элементы
// ---------------------------------------------------------------------------------------------

const CHECKBOX_PATH_ON =
  'M19 3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.11 0 2-.9 2-2V5c0-1.1-.89-2-2-2zm-9 14l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z';
const CHECKBOX_PATH_OFF =
  'M19 5v14H5V5h14m0-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2z';

export interface CellCheckboxProps {
  checked: boolean;
  onChange: () => void;
  ariaLabel?: string;
}

/** Чекбокс строки — вид MUI `Checkbox size="small"` (те же значки), без ButtonBase и ряби. */
export const CellCheckbox: React.FC<CellCheckboxProps> = ({ checked, onChange, ariaLabel }) => (
  <span
    role="checkbox"
    aria-checked={checked}
    aria-label={ariaLabel}
    tabIndex={0}
    className={checked ? 'gd-check gd-check--on' : 'gd-check'}
    onClick={onChange}
    onKeyDown={(event) => {
      if (event.key === ' ' || event.key === 'Enter') {
        event.preventDefault();
        onChange();
      }
    }}
  >
    <svg viewBox="0 0 24 24" focusable="false" aria-hidden>
      <path d={checked ? CHECKBOX_PATH_ON : CHECKBOX_PATH_OFF} />
    </svg>
  </span>
);

export interface CellSwitchProps {
  checked: boolean;
  disabled?: boolean;
  onChange: () => void;
  ariaLabel?: string;
}

/**
 * Переключатель — вид MUI `Switch size="small"` (размеры темы), без ButtonBase и ряби. Включённый —
 * зелёный, как все переключатели админки.
 */
export const CellSwitch: React.FC<CellSwitchProps> = ({ checked, disabled, onChange, ariaLabel }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={ariaLabel}
    disabled={disabled}
    className={checked ? 'gd-switch gd-switch--on' : 'gd-switch'}
    onClick={onChange}
  >
    <span className="gd-switch__base">
      <span className="gd-switch__thumb" />
    </span>
    <span className="gd-switch__track" />
  </button>
);

export interface CellIconButtonProps {
  /** Подсказка и подпись для экранного диктора. */
  title: string;
  color?: string;
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
  /** Значок: `<Delete fontSize="small" />` и т. п. */
  children: React.ReactNode;
}

/** Кнопка-значок в строке — вид MUI `IconButton size="small"` этой темы. */
export const CellIconButton: React.FC<CellIconButtonProps> = ({ title, color, onClick, children }) => (
  <CellTip title={title}>
    <button
      type="button"
      className="gd-icon-btn"
      aria-label={title}
      style={color ? { color } : undefined}
      onClick={onClick}
    >
      {children}
    </button>
  </CellTip>
);

// ---------------------------------------------------------------------------------------------
// Стили — один раз на всю админку
// ---------------------------------------------------------------------------------------------

/** Переопределение `root` компонента из темы — чтобы повторить его, а не переписывать цвета руками. */
const rootOverride = (theme: Theme, name: 'MuiTableCell' | 'MuiIconButton'): CSSObject => {
  const root = theme.components?.[name]?.styleOverrides?.root;
  return root && typeof root === 'object' ? (root as CSSObject) : {};
};

const buildCellStyles = (theme: Theme): CSSObject => {
  const hover = alpha(theme.palette.action.active, theme.palette.action.hoverOpacity);
  const shortest = theme.transitions.duration.shortest;

  return {
    // ---------- строки ----------
    '.gd-row': { color: 'inherit', verticalAlign: 'middle', outline: 0 },
    '.gd-row--stripe': { backgroundColor: ROW_STRIPE_BG },
    '.gd-row--conflict': {
      backgroundColor: TONE_BG.conflict,
      boxShadow: `inset 3px 0 0 0 ${TONE_BORDER.conflict}`,
    },
    '.gd-row--saved': { backgroundColor: TONE_BG.saved, boxShadow: `inset 3px 0 0 0 ${TONE_BORDER.saved}` },
    // Как hoverableRow: наведение перекрывает и полоску, и подсветку
    '.gd-row:hover': { backgroundColor: `${BG_COLORS.GLASS_DARK} !important` },
    '.gd-row-new': { backgroundColor: 'rgba(255, 255, 255, 0.04)' },

    // ---------- ячейки: как TableCell size="small" этой темы ----------
    '.gd-td': {
      ...theme.typography.body2,
      display: 'table-cell',
      verticalAlign: 'inherit',
      textAlign: 'left',
      padding: '6px 16px',
      color: theme.palette.text.primary,
      borderBottom: `1px solid ${darken(alpha(theme.palette.divider, 1), 0.68)}`,
      ...rootOverride(theme, 'MuiTableCell'),
    },
    '.gd-td--right': { textAlign: 'right' },
    '.gd-td--center': { textAlign: 'center' },
    '.gd-td--check': { width: 48, padding: '0 12px 0 16px' },
    '.gd-td--relative': { position: 'relative' },
    // Правку выдаёт вся ячейка целиком: она чуть светлее строки. Без линий и значков. Курсор —
    // «клетка», как в Excel: клик выделяет, правка — двойным кликом, Enter или набором (§2c)
    '.gd-editable': {
      position: 'relative',
      cursor: 'cell',
      '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.05)' },
    },
    // Ячейку правили в этом браузере: заливка держится до «Снять отметки». Ниже .gd-editable —
    // поверх его наведения, чтобы отметка не пропадала под курсором
    '.gd-td--changed': { backgroundColor: CHANGED_BG },
    '.gd-td--changed.gd-editable:hover': { backgroundColor: CHANGED_BG_HOVER },
    // Таблица берёт фокус клавиатуры ради выделения клеток; её фокус видно по рамке активной клетки
    '.gd-grid:focus': { outline: 'none' },
    // Выделенные клетки (копирование и вставка, docs/game-data.md §2c). Тонировка — тенью поверх
    // фона, а не фоном: зелёная отметка изменённой ячейки под выделением остаётся видна. Рамка
    // вокруг всего диапазона — тоже тенями, по краям (`gd-sel-t/b/l/r` у крайних клеток): border
    // прибавил бы к размеру ячейки, и таблица бы дёрнулась. Края складываются через переменные —
    // у угловой клетки их два, и одним классом с box-shadow их не сложить.
    '.gd-td--sel': {
      boxShadow: [
        `var(--gd-sel-t, ${NO_SHADOW})`,
        `var(--gd-sel-b, ${NO_SHADOW})`,
        `var(--gd-sel-l, ${NO_SHADOW})`,
        `var(--gd-sel-r, ${NO_SHADOW})`,
        `inset 0 0 0 9999px var(--gd-sel-tint, ${SELECTION_TINT})`,
      ].join(', '),
    },
    '.gd-sel-t': { '--gd-sel-t': `inset 0 2px 0 0 ${SELECTION_BORDER}` },
    '.gd-sel-b': { '--gd-sel-b': `inset 0 -2px 0 0 ${SELECTION_BORDER}` },
    '.gd-sel-l': { '--gd-sel-l': `inset 2px 0 0 0 ${SELECTION_BORDER}` },
    '.gd-sel-r': { '--gd-sel-r': `inset -2px 0 0 0 ${SELECTION_BORDER}` },
    // Активная клетка (с неё начали; Enter правит её) — в своей рамке. Залита, как остальные:
    // без заливки, как в Sheets, она выглядела выпавшей из выделения (владелец, 24.09.2026)
    '.gd-td--active': {
      '--gd-sel-t': `inset 0 2px 0 0 ${SELECTION_BORDER}`,
      '--gd-sel-b': `inset 0 -2px 0 0 ${SELECTION_BORDER}`,
      '--gd-sel-l': `inset 2px 0 0 0 ${SELECTION_BORDER}`,
      '--gd-sel-r': `inset -2px 0 0 0 ${SELECTION_BORDER}`,
    },

    // ---------- текст: как Typography body1 ----------
    '.gd-text': { ...theme.typography.body1, margin: 0 },
    '.gd-text--primary': { color: TEXT_COLORS.PRIMARY },
    '.gd-text--muted': { color: TEXT_COLORS.MUTED },
    '.gd-text--disabled': { color: TEXT_COLORS.DISABLED },
    '.gd-text--bold': { fontWeight: 600 },
    '.gd-text--small': {
      fontSize: FONT_SIZES.smallCaption.xs,
      [theme.breakpoints.up('sm')]: { fontSize: FONT_SIZES.smallCaption.sm },
    },
    '.gd-text--nowrap': { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },

    // ---------- раскладка ----------
    '.gd-flex': { display: 'flex', alignItems: 'center', gap: 4, minWidth: 0 },
    '.gd-iflex': { display: 'inline-flex', alignItems: 'center', gap: 4 },
    '.gd-stack': { display: 'flex', flexDirection: 'column', gap: 2 },
    '.gd-actions': { display: 'flex', justifyContent: 'flex-end', gap: 4 },
    '.gd-ref': { display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 },
    '.gd-ref__label': { minWidth: 0 },
    // Имя + доп. название в одну строку: имя ужимается с многоточием (`minWidth: 0` — иначе
    // flex-элемент не сжимается ниже содержимого и выдавливает соседей)
    '.gd-ref__name': { minWidth: 0 },
    // Подпись рядом со ссылкой (доп. название, «× 3», «Ур. 5»): сжиматься и переноситься должно
    // не она, а длинное название. Без `nowrap` «× 3» рвётся по пробелу на две строки — flex ужимает
    // элемент до min-content, а это ширина самого широкого слова, то есть одного символа
    '.gd-noshrink': { flexShrink: 0, whiteSpace: 'nowrap' },

    // ---------- чекбокс: как Checkbox size="small" ----------
    '.gd-check': {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      verticalAlign: 'middle',
      width: 20,
      height: 20,
      borderRadius: '50%',
      color: TEXT_COLORS.MUTED,
      cursor: 'pointer',
      outline: 0,
      '& svg': { width: 20, height: 20, fill: 'currentColor', flexShrink: 0, userSelect: 'none' },
      '&:hover, &:focus-visible': { backgroundColor: hover },
    },
    '.gd-check--on': { color: theme.palette.primary.main },

    // ---------- переключатель: как Switch size="small" ----------
    '.gd-switch': {
      position: 'relative',
      zIndex: 0,
      display: 'inline-flex',
      flexShrink: 0,
      boxSizing: 'border-box',
      width: 40,
      height: 24,
      padding: 7,
      margin: 0,
      border: 0,
      background: 'none',
      overflow: 'hidden',
      verticalAlign: 'middle',
      color: 'inherit',
      font: 'inherit',
      cursor: 'pointer',
      outline: 0,
      '&:disabled': { cursor: 'default' },
    },
    '.gd-switch__base': {
      position: 'absolute',
      top: 0,
      left: 0,
      zIndex: 1,
      display: 'inline-flex',
      padding: 4,
      borderRadius: '50%',
      color: theme.palette.grey[300],
      transition: theme.transitions.create(['left', 'transform', 'background-color'], { duration: shortest }),
    },
    '.gd-switch:hover .gd-switch__base, .gd-switch:focus-visible .gd-switch__base': { backgroundColor: hover },
    '.gd-switch__thumb': {
      width: 16,
      height: 16,
      borderRadius: '50%',
      backgroundColor: 'currentColor',
      boxShadow: theme.shadows[1],
    },
    '.gd-switch__track': {
      width: '100%',
      height: '100%',
      borderRadius: 7,
      zIndex: -1,
      backgroundColor: theme.palette.common.white,
      opacity: 0.3,
      transition: theme.transitions.create(['opacity', 'background-color'], { duration: shortest }),
    },
    // Включённый — зелёный, как переключатели магазина, рулетки и постов. Цвет темы (белый на 50%)
    // делал включённый серее выключенного — их путали (10.09.2026)
    '.gd-switch--on .gd-switch__base': { transform: 'translateX(16px)', color: ACTION_COLORS.ADD },
    '.gd-switch--on .gd-switch__track': { backgroundColor: ACTION_COLORS.ADD, opacity: 0.5 },
    '.gd-switch:disabled .gd-switch__base': { color: theme.palette.grey[600] },
    '.gd-switch:disabled .gd-switch__track': { opacity: 0.2 },

    // ---------- кнопка-значок: как IconButton size="small" этой темы ----------
    '.gd-icon-btn': {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      boxSizing: 'border-box',
      verticalAlign: 'middle',
      padding: 5,
      margin: 0,
      border: 0,
      borderRadius: '50%',
      background: 'transparent',
      color: 'inherit',
      fontSize: theme.typography.pxToRem(18),
      cursor: 'pointer',
      outline: 0,
      ...rootOverride(theme, 'MuiIconButton'),
      '&:focus-visible': { backgroundColor: hover },
      '&:active': { backgroundColor: alpha(theme.palette.action.active, 0.14) },
    },
  };
};

/**
 * Стили кирпичиков выше — один раз на всю админку (смонтированы в `AdminLayout`, рядом с
 * провайдером конфликтов). Строки их не пересобирают: класс в разметке ничего не стоит.
 */
export const GameDataCellStyles = React.memo(function GameDataCellStyles() {
  return <GlobalStyles styles={buildCellStyles} />;
});
